/**
 * 顶部栏：折叠按钮、面包屑、菜单搜索、全屏、主题切换、管理员菜单
 */
import { useMemo, useState, useSyncExternalStore } from 'react'
import { App, AutoComplete, Avatar, Breadcrumb, Button, Dropdown, Input, Tooltip, type MenuProps } from 'antd'
import {
  DownOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SkinOutlined,
  UserOutlined
} from '@ant-design/icons'
import { Link, useMatches, useNavigate } from '@tanstack/react-router'
import { useThemeStore, type ThemeMode } from '@/stores/theme'
import { getRoleDisplayName, getUserDisplayName, getUserInitials, useAuthStore } from '@/stores/auth'
import { buildMenu, flattenMenu } from '@/router/menu'
import { getMenuSources } from '@/router/routes'
import { ChangePasswordModal } from './ChangePasswordModal'
import styles from './Header.module.css'

const THEME_MODE_LABELS: Record<ThemeMode, string> = { auto: '跟随系统', dark: '暗色', light: '明亮' }

function subscribeFullscreen(onChange: () => void) {
  document.addEventListener('fullscreenchange', onChange)
  return () => document.removeEventListener('fullscreenchange', onChange)
}

interface HeaderProps {
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function Header({ collapsed, onToggleCollapsed }: Readonly<HeaderProps>) {
  const { message, modal } = App.useApp()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const themeMode = useThemeStore((state) => state.mode)
  const isFullscreen = useSyncExternalStore(subscribeFullscreen, () => !!document.fullscreenElement)
  const [keyword, setKeyword] = useState('')
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  // 面包屑：当前路由链上有标题的层级
  const crumbs = useMatches({
    select: (matches) =>
      matches
        .filter((match) => match.staticData.title)
        .map((match) => ({ title: match.staticData.title!, path: match.pathname }))
  })

  // 搜索建议：当前管理员可访问的菜单页
  const menuPages = useMemo(
    () => (user ? flattenMenu(buildMenu(getMenuSources(), useAuthStore.getState())) : []),
    [user]
  )
  const suggestions = keyword.trim()
    ? menuPages.filter((item) => item.title.toLowerCase().includes(keyword.trim().toLowerCase())).slice(0, 5)
    : []

  const goTo = (path: string) => {
    setKeyword('')
    navigate({ to: path })
  }

  // 回车：跳到第一个匹配的菜单；没有匹配时按关键词搜索用户
  const handleSearch = (value: string) => {
    const text = value.trim()
    if (!text) return
    if (suggestions[0]?.path) goTo(suggestions[0].path)
    else {
      setKeyword('')
      navigate({ to: '/users', search: { search: text } })
    }
  }

  const toggleTheme = () => {
    useThemeStore.getState().toggleMode()
    message.success(`已切换至${THEME_MODE_LABELS[useThemeStore.getState().mode]}主题`)
  }

  const toggleFullscreen = () => {
    if (isFullscreen) void document.exitFullscreen()
    else void document.documentElement.requestFullscreen()
  }

  const confirmLogout = () => {
    modal.confirm({
      title: '确认退出',
      content: '确定要退出登录吗？',
      icon: <LogoutOutlined />,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        await useAuthStore.getState().logout()
        message.success('已退出登录')
        await navigate({ to: '/login', search: {} })
      }
    })
  }

  const settingsMenu: MenuProps['items'] = [
    { key: 'theme', icon: <SkinOutlined />, label: `主题：${THEME_MODE_LABELS[themeMode]}`, onClick: toggleTheme },
    { type: 'divider' },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: '帮助中心',
      onClick: () => window.open('https://docs.acquilang.ai', '_blank', 'noopener,noreferrer')
    }
  ]

  const displayName = getUserDisplayName(user)
  const roleName = getRoleDisplayName(user?.role)
  const userMenu: MenuProps['items'] = [
    {
      key: 'info',
      disabled: true,
      label: (
        <div className={styles.userCard}>
          <div className={styles.userCardName}>{displayName}</div>
          <div className={styles.userCardMeta}>{roleName}</div>
          <div className={styles.userCardMeta}>{user?.email}</div>
        </div>
      )
    },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: '个人资料', onClick: () => goTo('/profile') },
    { key: 'password', icon: <KeyOutlined />, label: '修改密码', onClick: () => setChangePasswordOpen(true) },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true, onClick: confirmLogout }
  ]

  return (
    <div className={styles.header}>
      <div className={styles.left}>
        <Button
          type="text"
          aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapsed}
        />
        <Breadcrumb
          items={crumbs.map((crumb, index) => ({
            key: crumb.path,
            title: index < crumbs.length - 1 ? <Link to={crumb.path}>{crumb.title}</Link> : crumb.title
          }))}
        />
      </div>

      <div className={styles.right}>
        <AutoComplete
          className={styles.search}
          value={keyword}
          options={suggestions.map((item) => ({ value: item.path!, label: item.title }))}
          onChange={setKeyword}
          onSelect={(path: string) => goTo(path)}
        >
          <Input.Search placeholder="搜索菜单、用户..." onSearch={handleSearch} />
        </AutoComplete>

        <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
          <Button
            type="text"
            size="large"
            aria-label={isFullscreen ? '退出全屏' : '全屏'}
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
          />
        </Tooltip>

        <Dropdown menu={{ items: settingsMenu }} trigger={['click']} placement="bottomRight">
          <Button type="text" size="large" aria-label="设置" icon={<SettingOutlined />} />
        </Dropdown>

        <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
          <button type="button" className={styles.userInfo} aria-label="管理员菜单">
            <Avatar size={32} src={user?.avatar}>
              {getUserInitials(user)}
            </Avatar>
            <span className={styles.userDetails}>
              <span className={styles.userName}>{displayName}</span>
              <span className={styles.userRole}>{roleName}</span>
            </span>
            <DownOutlined className={styles.dropdownIcon} />
          </button>
        </Dropdown>
      </div>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </div>
  )
}
